"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Heading } from "@/components/ui/typography";

export default function ApiTestPage() {
  const t = useTranslations("apiTestPage");
  const tc = (key: string) => t(`common.${key}` as any, {}) as string;

  const searchParams = useSearchParams();
  const isDev = process.env.NODE_ENV !== "production";
  const h1w = searchParams?.get("h1w");
  const h1WeightClass = useMemo(() => {
    if (!isDev) return undefined;
    switch ((h1w || "").toLowerCase()) {
      case "bold":
        return "font-bold";
      case "semibold":
        return "font-semibold";
      case "medium":
        return "font-medium";
      default:
        return undefined;
    }
  }, [h1w, isDev]);

  // 聊天API测试状态
  const [chatInput, setChatInput] = useState("");
  const [chatLevel, setChatLevel] = useState("intermediate");
  const [chatResponse, setChatResponse] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // 对话练习API测试状态
  const [dialogueScenario, setDialogueScenario] = useState("");
  const [dialogueLevel, setDialogueLevel] = useState("intermediate");
  const [dialogueResponse, setDialogueResponse] = useState("");
  const [dialogueLoading, setDialogueLoading] = useState(false);

  // 学习计划API测试状态
  const [planLevel, setPlanLevel] = useState("intermediate");
  const [planGoals, setPlanGoals] = useState("");
  const [planTime, setPlanTime] = useState("");
  const [planInterests, setPlanInterests] = useState("");
  const [planResponse, setPlanResponse] = useState("");
  const [planLoading, setPlanLoading] = useState(false);

  // 翻译评估API测试状态
  const [originalText, setOriginalText] = useState("");
  const [userTranslation, setUserTranslation] = useState("");
  const [translationDirection, setTranslationDirection] = useState("zh-to-en");
  const [translationResponse, setTranslationResponse] = useState("");
  const [translationLoading, setTranslationLoading] = useState(false);

  // 词汇学习API测试状态
  const [vocabularyWords, setVocabularyWords] = useState("");
  const [vocabularyLevel, setVocabularyLevel] = useState("intermediate");
  const [vocabularyResponse, setVocabularyResponse] = useState("");
  const [vocabularyLoading, setVocabularyLoading] = useState(false);

  // 写作练习API测试状态
  const [writingTopic, setWritingTopic] = useState("");
  const [writingLevel, setWritingLevel] = useState("intermediate");
  const [writingResponse, setWritingResponse] = useState("");
  const [writingLoading, setWritingLoading] = useState(false);

  // 测试聊天API
  const testChatApi = async () => {
    if (!chatInput) return;
    setChatLoading(true);
    setChatResponse("");

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: chatInput,
          userLevel: chatLevel,
          history: [],
        }),
      });

      const data = await response.json();
      setChatResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setChatResponse(`Error: ${(error as Error).message}`);
    } finally {
      setChatLoading(false);
    }
  };

  // 测试对话练习API
  const testDialogueApi = async () => {
    if (!dialogueScenario) return;
    setDialogueLoading(true);
    setDialogueResponse("");

    try {
      const response = await fetch("/api/dialogue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          scenario: dialogueScenario,
          userLevel: dialogueLevel,
        }),
      });

      const data = await response.json();
      setDialogueResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setDialogueResponse(`Error: ${(error as Error).message}`);
    } finally {
      setDialogueLoading(false);
    }
  };

  // 测试学习计划API
  const testLearningPlanApi = async () => {
    if (!planGoals || !planTime || !planInterests) return;
    setPlanLoading(true);
    setPlanResponse("");

    try {
      const response = await fetch("/api/learning-plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userLevel: planLevel,
          goals: planGoals,
          timeAvailable: planTime,
          interests: planInterests,
        }),
      });

      const data = await response.json();
      setPlanResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setPlanResponse(`Error: ${(error as Error).message}`);
    } finally {
      setPlanLoading(false);
    }
  };

  // 测试翻译评估API
  const testTranslationApi = async () => {
    if (!originalText || !userTranslation) return;
    setTranslationLoading(true);
    setTranslationResponse("");

    try {
      const response = await fetch("/api/translation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          originalText,
          userTranslation,
          direction: translationDirection,
        }),
      });

      const data = await response.json();
      setTranslationResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setTranslationResponse(`Error: ${(error as Error).message}`);
    } finally {
      setTranslationLoading(false);
    }
  };

  // 测试词汇学习API
  const testVocabularyApi = async () => {
    if (!vocabularyWords) return;
    setVocabularyLoading(true);
    setVocabularyResponse("");

    try {
      const response = await fetch("/api/vocabulary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          words: vocabularyWords.split(",").map((word) => word.trim()),
          userLevel: vocabularyLevel,
        }),
      });

      const data = await response.json();
      setVocabularyResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setVocabularyResponse(`Error: ${(error as Error).message}`);
    } finally {
      setVocabularyLoading(false);
    }
  };

  // 测试写作练习API
  const testWritingApi = async () => {
    if (!writingTopic) return;
    setWritingLoading(true);
    setWritingResponse("");

    try {
      const response = await fetch("/api/writing", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          topic: writingTopic,
          userLevel: writingLevel,
        }),
      });

      const data = await response.json();
      setWritingResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setWritingResponse(`Error: ${(error as Error).message}`);
    } finally {
      setWritingLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8">
      <div className="space-y-2">
        <Heading
          variant="h1"
          as="h1"
          className={`text-foreground font-semibold ${h1WeightClass ?? ""}`}
        >
          {t("title")}
        </Heading>
        <p className="text-muted-foreground">{t("description")}</p>
      </div>

      <Tabs defaultValue="chat" className="w-full mt-6">
        <TabsList>
          <TabsTrigger value="chat">{t("tabs.chat")}</TabsTrigger>
          <TabsTrigger value="dialogue">{t("tabs.dialogue")}</TabsTrigger>
          <TabsTrigger value="learning-plan">
            {t("tabs.study_plan")}
          </TabsTrigger>
          <TabsTrigger value="translation">{t("tabs.translation")}</TabsTrigger>
          <TabsTrigger value="vocabulary">{t("tabs.vocabulary")}</TabsTrigger>
          <TabsTrigger value="writing" className="bg-gray-100">
            {t("tabs.writing")}
          </TabsTrigger>
        </TabsList>

        {/* 聊天 API 测试 */}
        <TabsContent value="chat" className="mt-20 md:mt-10">
          <Card>
            <CardHeader>
              <CardTitle>{t("chat.title")}</CardTitle>
              <CardDescription>{t("chat.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="chat-input">{t("chat.message")}</Label>
                  <Textarea
                    id="chat-input"
                    placeholder={t("chat.placeholder")}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="chat-level">{t("chat.level")}</Label>
                  <Select value={chatLevel} onValueChange={setChatLevel}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_level")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">
                        {t("levels.beginner")}
                      </SelectItem>
                      <SelectItem value="intermediate">
                        {t("levels.intermediate")}
                      </SelectItem>
                      <SelectItem value="advanced">
                        {t("levels.advanced")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={testChatApi}
                  disabled={chatLoading}
                  className="bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                >
                  {chatLoading ? t("loading") : t("send")}
                </Button>

                {chatResponse && (
                  <div className="mt-4">
                    <Label>{t("response")}</Label>
                    <div className="p-4 bg-gray-100 rounded-md mt-2 overflow-auto max-h-96">
                      <pre>{chatResponse}</pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 对话练习 API 测试 */}
        <TabsContent value="dialogue" className="mt-20 md:mt-10">
          <Card>
            <CardHeader>
              <CardTitle>{t("dialogue.title")}</CardTitle>
              <CardDescription>{t("dialogue.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="dialogue-scenario">
                    {t("dialogue.scenario")}
                  </Label>
                  <Input
                    id="dialogue-scenario"
                    placeholder={t("dialogue.placeholder")}
                    value={dialogueScenario}
                    onChange={(e) => setDialogueScenario(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="dialogue-level">{t("dialogue.level")}</Label>
                  <Select
                    value={dialogueLevel}
                    onValueChange={setDialogueLevel}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_level")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">
                        {t("levels.beginner")}
                      </SelectItem>
                      <SelectItem value="intermediate">
                        {t("levels.intermediate")}
                      </SelectItem>
                      <SelectItem value="advanced">
                        {t("levels.advanced")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={testDialogueApi}
                  disabled={dialogueLoading}
                  className="bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                >
                  {dialogueLoading ? t("loading") : t("dialogue.generate")}
                </Button>

                {dialogueResponse && (
                  <div className="mt-4">
                    <Label>{t("response")}</Label>
                    <div className="p-4 bg-gray-100 rounded-md mt-2 overflow-auto max-h-96">
                      <pre>{dialogueResponse}</pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 学习计划 API 测试 */}
        <TabsContent value="learning-plan" className="mt-20 md:mt-10">
          <Card>
            <CardHeader>
              <CardTitle>{t("learning_plan.title")}</CardTitle>
              <CardDescription>
                {t("learning_plan.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="plan-level">{t("level")}</Label>
                  <Select value={planLevel} onValueChange={setPlanLevel}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_level")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">
                        {t("levels.beginner")}
                      </SelectItem>
                      <SelectItem value="intermediate">
                        {t("levels.intermediate")}
                      </SelectItem>
                      <SelectItem value="advanced">
                        {t("levels.advanced")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="plan-goals">{t("learning_plan.goals")}</Label>
                  <Textarea
                    id="plan-goals"
                    placeholder={t("learning_plan.goals_placeholder")}
                    value={planGoals}
                    onChange={(e) => setPlanGoals(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="plan-time">{t("learning_plan.time")}</Label>
                  <Input
                    id="plan-time"
                    placeholder={t("learning_plan.time_placeholder")}
                    value={planTime}
                    onChange={(e) => setPlanTime(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="plan-interests">
                    {t("learning_plan.interests")}
                  </Label>
                  <Textarea
                    id="plan-interests"
                    placeholder={t("learning_plan.interests_placeholder")}
                    value={planInterests}
                    onChange={(e) => setPlanInterests(e.target.value)}
                  />
                </div>

                <Button
                  onClick={testLearningPlanApi}
                  disabled={planLoading}
                  className="bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                >
                  {planLoading ? t("loading") : t("learning_plan.generate")}
                </Button>

                {planResponse && (
                  <div className="mt-4">
                    <Label>{t("response")}</Label>
                    <div className="p-4 bg-gray-100 rounded-md mt-2 overflow-auto max-h-96">
                      <pre>{planResponse}</pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 翻译评估 API 测试 */}
        <TabsContent value="translation" className="mt-20 md:mt-10">
          <Card>
            <CardHeader>
              <CardTitle>{t("translation.title")}</CardTitle>
              <CardDescription>{t("translation.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="translation-direction">
                    {t("translation.direction")}
                  </Label>
                  <Select
                    value={translationDirection}
                    onValueChange={setTranslationDirection}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("translation.select_direction")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zh-to-en">
                        {t("translation.zh_to_en")}
                      </SelectItem>
                      <SelectItem value="en-to-zh">
                        {t("translation.en_to_zh")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="original-text">
                    {t("translation.original")}
                  </Label>
                  <Textarea
                    id="original-text"
                    placeholder={t("translation.original_placeholder")}
                    value={originalText}
                    onChange={(e) => setOriginalText(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="user-translation">
                    {t("translation.your_translation")}
                  </Label>
                  <Textarea
                    id="user-translation"
                    placeholder={t("translation.translation_placeholder")}
                    value={userTranslation}
                    onChange={(e) => setUserTranslation(e.target.value)}
                  />
                </div>

                <Button
                  onClick={testTranslationApi}
                  disabled={translationLoading}
                  className="bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                >
                  {translationLoading
                    ? t("loading")
                    : t("translation.evaluate")}
                </Button>

                {translationResponse && (
                  <div className="mt-4">
                    <Label>{t("response")}</Label>
                    <div className="p-4 bg-gray-100 rounded-md mt-2 overflow-auto max-h-96">
                      <pre>{translationResponse}</pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 词汇学习 API 测试 */}
        <TabsContent value="vocabulary" className="mt-20 md:mt-10">
          <Card>
            <CardHeader>
              <CardTitle>{t("vocabulary.title")}</CardTitle>
              <CardDescription>{t("vocabulary.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="vocabulary-words">
                    {t("vocabulary.words")}
                  </Label>
                  <Textarea
                    id="vocabulary-words"
                    placeholder={t("vocabulary.words_placeholder")}
                    value={vocabularyWords}
                    onChange={(e) => setVocabularyWords(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="vocabulary-level">{t("level")}</Label>
                  <Select
                    value={vocabularyLevel}
                    onValueChange={setVocabularyLevel}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_level")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">
                        {t("levels.beginner")}
                      </SelectItem>
                      <SelectItem value="intermediate">
                        {t("levels.intermediate")}
                      </SelectItem>
                      <SelectItem value="advanced">
                        {t("levels.advanced")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={testVocabularyApi}
                  disabled={vocabularyLoading}
                  className="bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                >
                  {vocabularyLoading ? t("loading") : t("vocabulary.generate")}
                </Button>

                {vocabularyResponse && (
                  <div className="mt-4">
                    <Label>{t("response")}</Label>
                    <div className="p-4 bg-gray-100 rounded-md mt-2 overflow-auto max-h-96">
                      <pre>{vocabularyResponse}</pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 写作练习 API 测试 */}
        <TabsContent value="writing" className="mt-20 md:mt-10">
          <Card>
            <CardHeader>
              <CardTitle>{t("writing.title")}</CardTitle>
              <CardDescription>{t("writing.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="writing-topic">{t("writing.topic")}</Label>
                  <Input
                    id="writing-topic"
                    placeholder={t("writing.topic_placeholder")}
                    value={writingTopic}
                    onChange={(e) => setWritingTopic(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="writing-level">{t("level")}</Label>
                  <Select value={writingLevel} onValueChange={setWritingLevel}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_level")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">
                        {t("levels.beginner")}
                      </SelectItem>
                      <SelectItem value="intermediate">
                        {t("levels.intermediate")}
                      </SelectItem>
                      <SelectItem value="advanced">
                        {t("levels.advanced")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={testWritingApi}
                  disabled={writingLoading}
                  className="bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                >
                  {writingLoading ? t("loading") : t("writing.generate")}
                </Button>

                {writingResponse && (
                  <div className="mt-4">
                    <Label>{t("response")}</Label>
                    <div className="p-4 bg-gray-100 rounded-md mt-2 overflow-auto max-h-96">
                      <pre>{writingResponse}</pre>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
